import { useState } from 'react'
import axios from 'axios'
import { ScrollText } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from '@/components/ui/empty'
import { Skeleton } from '@/components/ui/skeleton'
import { DocumentEditor } from '@/components/document-editor'
import { ComplianceSubPageHeader } from '@/features/compliance-studio/machinery-directive/components/ComplianceSubPageHeader'
import { ComplianceSubPageTitle } from '@/features/compliance-studio/machinery-directive/components/ComplianceSubPageTitle'
import {
  useExportCraDeclarationOfConformityPdf,
  useGenerateCraDeclarationOfConformity,
  useGetCraDeclarationOfConformity,
  useSaveCraDeclarationOfConformity,
} from './api'

export function CraDeclarationOfConformityPage() {
  const { craStudioId = '' } = useParams()
  const { t } = useTranslation('craCompliance')
  const declarationQuery = useGetCraDeclarationOfConformity(craStudioId)
  const generateDeclaration = useGenerateCraDeclarationOfConformity(craStudioId)
  const saveDeclaration = useSaveCraDeclarationOfConformity(craStudioId)
  const exportPdf = useExportCraDeclarationOfConformityPdf(craStudioId)
  const [regenerateOpen, setRegenerateOpen] = useState(false)
  const [declarationMarkdownSyncKey, setDeclarationMarkdownSyncKey] =
    useState(0)

  const storedMarkdown = declarationQuery.data?.markdown?.trim() ?? ''
  const displayMarkdown = declarationQuery.data?.markdown ?? ''
  const hasDocument = storedMarkdown.length > 0

  const runGenerate = () => {
    generateDeclaration.mutate(undefined, {
      onSuccess: () => {
        setDeclarationMarkdownSyncKey((key) => key + 1)
        toast.success(t('declarationOfConformity.generateSuccess'))
      },
      onError: (error) => {
        let message = t('declarationOfConformity.generateFailed')
        if (axios.isAxiosError(error)) {
          const responseMessage = (
            error.response?.data as { message?: string } | undefined
          )?.message
          if (typeof responseMessage === 'string' && responseMessage.length > 0) {
            message = responseMessage
          }
        }
        toast.error(message)
      },
    })
  }

  const handleGenerateClick = () => {
    if (hasDocument) {
      setRegenerateOpen(true)
      return
    }
    runGenerate()
  }

  const handleSave = async (markdown: string) => {
    try {
      await saveDeclaration.mutateAsync(markdown)
      toast.success(t('declarationOfConformity.saveSuccess'))
    } catch (error) {
      let message = t('declarationOfConformity.saveFailed')
      if (axios.isAxiosError(error)) {
        const responseMessage = (
          error.response?.data as { message?: string } | undefined
        )?.message
        if (typeof responseMessage === 'string' && responseMessage.length > 0) {
          message = responseMessage
        }
      }
      toast.error(message)
    }
  }

  if (declarationQuery.isLoading) {
    return (
      <div className='space-y-4'>
        <Skeleton className='h-8 w-64' />
        <Skeleton className='h-48 w-full' />
      </div>
    )
  }

  let exportLabel = t('declarationOfConformity.exportPdf')
  if (exportPdf.isPending) {
    exportLabel = t('declarationOfConformity.exporting')
  }

  return (
    <div className='flex h-full flex-col space-y-4'>
      <ComplianceSubPageHeader>
        <ComplianceSubPageTitle>
          {t('nav.declarationOfConformity')}
        </ComplianceSubPageTitle>
        <div className='flex flex-wrap items-center gap-2'>
          {!hasDocument && (
            <Button
              onClick={handleGenerateClick}
              disabled={generateDeclaration.isPending}
            >
              {t('declarationOfConformity.generate')}
            </Button>
          )}
          {hasDocument && (
            <>
              <Button
                onClick={() => {
                  exportPdf.mutate(undefined, {
                    onSuccess: () => {
                      toast.success(t('declarationOfConformity.pdfExportedSuccess'))
                    },
                    onError: (error) => {
                      toast.error(
                        error.message || t('declarationOfConformity.exportFailed')
                      )
                    },
                  })
                }}
                variant='outline'
                disabled={exportPdf.isPending}
              >
                {exportLabel}
              </Button>
              <Button
                onClick={handleGenerateClick}
                variant='outline'
                disabled={generateDeclaration.isPending}
              >
                {t('declarationOfConformity.regenerate')}
              </Button>
            </>
          )}
        </div>
      </ComplianceSubPageHeader>

      {!hasDocument && (
        <Empty className='min-h-[320px] rounded-lg border border-dashed'>
          <EmptyHeader>
            <EmptyMedia variant='icon'>
              <ScrollText className='size-10' />
            </EmptyMedia>
            <EmptyTitle>{t('declarationOfConformity.emptyTitle')}</EmptyTitle>
            <EmptyDescription>
              {t('declarationOfConformity.emptyDescription')}
            </EmptyDescription>
          </EmptyHeader>
          <EmptyContent>
            <Button
              onClick={handleGenerateClick}
              disabled={generateDeclaration.isPending}
              size='sm'
              className='gap-2'
            >
              <ScrollText className='size-4' />
              {t('declarationOfConformity.generate')}
            </Button>
          </EmptyContent>
        </Empty>
      )}

      {hasDocument && (
        <DocumentEditor
          contentSyncKey={declarationMarkdownSyncKey}
          markdown={displayMarkdown}
          onSave={handleSave}
          isSaving={saveDeclaration.isPending}
          saveLabel={t('declarationOfConformity.save')}
          savingLabel={t('declarationOfConformity.saving')}
        />
      )}

      <AlertDialog open={regenerateOpen} onOpenChange={setRegenerateOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t('declarationOfConformity.regenerateConfirmTitle')}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t('declarationOfConformity.regenerateConfirmDescription')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>
              {t('declarationOfConformity.regenerateCancel')}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                setRegenerateOpen(false)
                runGenerate()
              }}
            >
              {t('declarationOfConformity.regenerateConfirm')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
